import React, { useState, useEffect, useRef, useCallback } from "react";
import {
  Alert,
  InteractionManager,
  Pressable,
  TextInput,
  View,
} from "react-native";
import color from "@constants/color";
import Text from "@components/CustomText";
import CityMallLogoBlack from "@assets/svg/CityMallLogoBlack";
import Warning from "@assets/svg/Warning";
import toast from "@utils/toast";
import { checkPhone, getBlockedErrorMessage } from "@services/onboardingApi";
import { useOnboarding } from "./OnboardingContext";
import { useActiveFlow } from "./ActiveFlowContext";
import { loginStyles as styles, placeholderColor } from "../Login/styles";
import type { StackNavigationProp } from "@react-navigation/stack";
import type { OnboardingStackParamList } from "./types";

interface OnboardingPhoneProps {
  navigation: StackNavigationProp<OnboardingStackParamList, "OnboardingPhone">;
}

const OnboardingPhone: React.FC<OnboardingPhoneProps> = ({ navigation }) => {
  const { updateData, resetData } = useOnboarding();
  const { resetFlow } = useActiveFlow();
  const [phoneNumber, setPhoneNumber] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isFocused, setIsFocused] = useState(false);
  const [hasError, setHasError] = useState(false);
  const inputRef = useRef<TextInput>(null);

  useEffect(() => {
    resetData();
    resetFlow();
    InteractionManager.runAfterInteractions(() => {
      inputRef.current?.focus();
    });
  }, [resetData, resetFlow]);

  useEffect(() => {
    if (hasError && phoneNumber.length <= 10) {
      setHasError(false);
    }
  }, [phoneNumber, hasError]);

  const formatPhoneNumber = useCallback((text: string) => {
    const cleaned = text.replace(/[^\d]/g, "");
    if (cleaned.startsWith("91") && cleaned.length > 10) {
      return cleaned.slice(2, 12);
    }
    return cleaned.slice(0, 10);
  }, []);

  const onChangeText = useCallback(
    (text: string) => {
      setPhoneNumber(formatPhoneNumber(text));
    },
    [formatPhoneNumber],
  );

  const handleCheckPhoneResult = useCallback(
    (result: Awaited<ReturnType<typeof checkPhone>>) => {
      switch (result.case) {
        case "active_cp":
          Alert.alert(
            "Pehle se CityMall Partner",
            "Aap pehle se CityMall Partner hain. Kya aap CityMall Delivery Partner banna chahte hain?\n\nDhyaan dein: 'Haan' select karne par aapka current CityMall Partner account delete ho jayega.",
            [
              { text: "Nahi", style: "cancel" },
              {
                text: "Haan",
                style: "destructive",
                onPress: () => {
                  updateData({ isCpConversion: true });
                  navigation.navigate("OnboardingOTP", {
                    phoneNumber,
                    skipKyc: false,
                  });
                },
              },
            ],
          );
          break;

        case "active_db":
          Alert.alert(
            "Pehle se Active DB",
            `Yeh number pehle se registered hai: ${result.registeredPhone}. Please login karein.`,
            [{ text: "Theek Hai", style: "cancel" }],
          );
          break;

        case "inactive_db_kyc_verified":
        case "inactive_cp_kyc_verified":
          // KYC already done — skip the KYC flow, go straight to spoke selection
          navigation.navigate("OnboardingOTP", {
            phoneNumber,
            skipKyc: true,
          });
          break;

        case "inactive_db_kyc_not_verified":
        case "inactive_cp_kyc_not_verified":
        case "new_user":
        default:
          navigation.navigate("OnboardingOTP", {
            phoneNumber,
            skipKyc: false,
          });
          break;
      }
    },
    [phoneNumber, navigation, updateData],
  );

  const sendOtp = useCallback(async () => {
    if (isSubmitting) return;

    updateData({ phoneNumber });

    setIsSubmitting(true);
    try {
      const result = await checkPhone(phoneNumber);
      handleCheckPhoneResult(result);
    } catch (checkError: unknown) {
      // Fraud-flagged identity — terminal, don't fall through to send-otp.
      const blockedMessage = getBlockedErrorMessage(checkError);
      if (blockedMessage) {
        toast.showLongToast(blockedMessage);
        setHasError(true);
        setIsSubmitting(false);
        return;
      }
      try {
        const { sendOtp: sendOtpApi } = await import("@services/onboardingApi");
        await sendOtpApi(phoneNumber);
        navigation.navigate("OnboardingOTP", { phoneNumber, skipKyc: false });
      } catch (otpError: unknown) {
        const axiosError = otpError as {
          response?: { data?: { message?: string } };
        };
        const message = axiosError?.response?.data?.message;
        if (message) {
          toast.showLongToast(message);
        }
        setHasError(true);
      }
    } finally {
      setIsSubmitting(false);
    }
  }, [
    isSubmitting,
    phoneNumber,
    updateData,
    handleCheckPhoneResult,
    navigation,
  ]);

  return (
    <View style={styles.container}>
      <View style={styles.heroArea}>
        <View style={styles.logoContainer}>
          <CityMallLogoBlack height={30} width={114} />
        </View>
      </View>

      <View style={styles.bottomCard}>
        <Text variant="title_18" style={styles.title}>
          Create New Account
        </Text>
        <Text variant="body_text_3" style={styles.subtitle}>
          Enter phone number to send OTP
        </Text>

        <Text variant="body_text_3" style={styles.phoneLabel}>
          Phone Number
        </Text>
        <View
          style={[
            styles.phoneInputContainer,
            isFocused && styles.phoneInputContainerFocused,
            hasError && styles.phoneInputContainerError,
          ]}
        >
          <Text style={styles.countryCode}>+91</Text>
          <View style={styles.separator} />
          <TextInput
            ref={inputRef}
            value={phoneNumber}
            style={styles.phoneInput}
            maxLength={10}
            keyboardType="number-pad"
            onChangeText={onChangeText}
            autoComplete="tel"
            placeholder="Enter number"
            placeholderTextColor={placeholderColor}
            onFocus={() => setIsFocused(true)}
            onBlur={() => setIsFocused(false)}
          />
        </View>

        {hasError && (
          <View style={styles.errorContainer}>
            <Warning color={color.errorRed} />
            <Text variant="caption_2" style={styles.errorText}>
              Please enter a valid phone number
            </Text>
          </View>
        )}

        <Pressable
          style={[styles.getOtpButton, { backgroundColor: color.primary }]}
          disabled={isSubmitting}
          onPress={sendOtp}
          accessibilityRole="button"
          accessibilityLabel="Get OTP"
        >
          <Text variant="title_16" style={styles.getOtpButtonText}>
            Get OTP
          </Text>
        </Pressable>
      </View>
    </View>
  );
};

export default OnboardingPhone;
